import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Trash2, Database, Save, Sparkles, Mail, PlugZap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TABS = [
  { key: 'praxis', label: 'Praxis' },
  { key: 'medidata', label: 'MediData-Versand' },
  { key: 'feedback', label: 'Rückmeldungen' },
  { key: 'reminder', label: 'Erinnerungs-E-Mail' },
  { key: 'insurers', label: 'Kostenträger' },
  { key: 'tariffs', label: 'Tarifpositionen' },
  { key: 'taxpoints', label: 'Taxpunktwerte' },
];

const PhysioBillingSettingsPage = ({ embedded = false }) => {
  const [tab, setTab] = useState('praxis');
  const [settings, setSettings] = useState(null);
  const [insurers, setInsurers] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [taxpoints, setTaxpoints] = useState([]);
  const [medidata, setMedidata] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [aiDrafting, setAiDrafting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, i, t, x, m, r] = await Promise.all([
        axios.get(`${API}/physio-billing/settings`, { headers: authHeaders() }),
        axios.get(`${API}/physio-billing/insurers`, { headers: authHeaders() }),
        axios.get(`${API}/physio-billing/tariff-positions`, { headers: authHeaders() }),
        axios.get(`${API}/physio-billing/tax-point-values`, { headers: authHeaders() }),
        axios.get(`${API}/physio-billing/medidata/settings`, { headers: authHeaders() }),
        axios.get(`${API}/physio-billing/reminder-settings`, { headers: authHeaders() }),
      ]);
      setSettings(s.data || {}); setInsurers(i.data || []); setTariffs(t.data || []);
      setTaxpoints(x.data || []); setMedidata(m.data || {}); setReminder(r.data || {});
    } catch (e) { toast.error('Laden fehlgeschlagen'); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveMedidata = async () => {
    try {
      const res = await axios.put(`${API}/physio-billing/medidata/settings`, { ...medidata }, { headers: authHeaders() });
      setMedidata(res.data);
      toast.success('MediData-Einstellungen gespeichert');
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
  };
  const [testingConn, setTestingConn] = useState(false);
  const testMedidata = async () => {
    setTestingConn(true);
    try {
      const res = await axios.post(`${API}/physio-billing/medidata/test-connection`, {}, { headers: authHeaders() });
      res.data.ok ? toast.success(res.data.message || 'Verbindung erfolgreich')
                  : toast.error(res.data.message || 'Verbindung fehlgeschlagen');
    } catch (e) { toast.error(e.response?.data?.detail || 'Verbindungstest fehlgeschlagen'); }
    finally { setTestingConn(false); }
  };
  const mf = (k) => (medidata || {})[k] ?? '';
  const setM = (k, v) => setMedidata(s => ({ ...s, [k]: v }));
  const readPem = (k, file) => {
    if (!file) return;
    if (file.size > 256 * 1024) { toast.error('Datei zu gross (max. 256 KB)'); return; }
    const r = new FileReader();
    r.onload = () => setM(k, String(r.result || ''));
    r.readAsText(file);
  };

  const [downloads, setDownloads] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const loadFeedback = useCallback(async () => {
    setFeedbackLoading(true);
    try {
      const [d, n] = await Promise.all([
        axios.get(`${API}/physio-billing/medidata/downloads`, { headers: authHeaders() }),
        axios.get(`${API}/physio-billing/medidata/notifications`, { headers: authHeaders() }),
      ]);
      setDownloads(d.data?.items || []);
      setNotifications(n.data?.items || []);
      if (d.data?.ok === false || n.data?.ok === false) {
        toast.error(d.data?.message || n.data?.message || 'MediData-Abruf fehlgeschlagen');
      } else if (d.data?.mode === 'test') {
        toast.info('Simuliert – kein Live-Zugang aktiv.');
      }
    } catch (e) { toast.error('Rückmeldungen konnten nicht geladen werden'); }
    finally { setFeedbackLoading(false); }
  }, []);
  const confirmDownload = async (ref) => {
    try {
      const res = await axios.post(`${API}/physio-billing/medidata/downloads/${encodeURIComponent(ref)}/confirm`, {}, { headers: authHeaders() });
      res.data?.ok === false ? toast.error(res.data?.message || 'Bestätigung fehlgeschlagen')
                             : toast.success('Rückmeldung bestätigt');
      loadFeedback();
    } catch (e) { toast.error('Bestätigung fehlgeschlagen'); }
  };
  const confirmNotification = async (id) => {
    try {
      const res = await axios.post(`${API}/physio-billing/medidata/notifications/${encodeURIComponent(id)}/confirm`, {}, { headers: authHeaders() });
      res.data?.ok === false ? toast.error(res.data?.message || 'Bestätigung fehlgeschlagen')
                             : toast.success('Nachricht bestätigt');
      loadFeedback();
    } catch (e) { toast.error('Bestätigung fehlgeschlagen'); }
  };
  useEffect(() => { if (tab === 'feedback') loadFeedback(); }, [tab, loadFeedback]);

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/physio-billing/settings`, settings, { headers: authHeaders() });
      toast.success('Praxisdaten gespeichert');
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
  };

  const seed = async () => {
    try {
      const res = await axios.post(`${API}/physio-billing/seed`, {}, { headers: authHeaders() });
      toast.success('Stammdaten geladen');
      loadAll();
    } catch (e) { toast.error('Seed fehlgeschlagen'); }
  };

  const addRow = async (coll, body, setter, list) => {
    try {
      const res = await axios.post(`${API}/physio-billing/${coll}`, body, { headers: authHeaders() });
      setter([...list, res.data]);
    } catch (e) { toast.error('Hinzufügen fehlgeschlagen'); }
  };
  const delRow = async (coll, id, setter, list) => {
    try {
      await axios.delete(`${API}/physio-billing/${coll}/${id}`, { headers: authHeaders() });
      setter(list.filter(r => r.id !== id));
    } catch (e) { toast.error('Löschen fehlgeschlagen'); }
  };

  const sf = (k) => (settings || {})[k] || '';
  const setS = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  const rf = (k) => (reminder || {})[k] ?? '';
  const setR = (k, v) => setReminder(s => ({ ...s, [k]: v }));
  const saveReminder = async () => {
    try {
      const res = await axios.put(`${API}/physio-billing/reminder-settings`, {
        enabled: !!reminder.enabled,
        threshold: parseInt(reminder.threshold) || 1,
        subject: reminder.subject || '',
        body: reminder.body || '',
      }, { headers: authHeaders() });
      setReminder(res.data);
      toast.success('Erinnerungs-E-Mail gespeichert');
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
  };
  const aiDraftReminder = async () => {
    setAiDrafting(true);
    try {
      const res = await axios.post(`${API}/physio-billing/reminder-settings/ai-draft`, {}, { headers: authHeaders() });
      setReminder(s => ({ ...s, subject: res.data.subject, body: res.data.body }));
      toast.success('KI-Entwurf eingefügt – bitte prüfen und speichern');
    } catch (e) { toast.error(e.response?.data?.detail || 'KI-Entwurf fehlgeschlagen'); }
    finally { setAiDrafting(false); }
  };

  return (
    <div className={embedded ? '' : 'p-6 max-w-4xl mx-auto'} data-testid="physio-billing-settings">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Physio-Abrechnung — Stammdaten</h2>
        <Button size="sm" variant="outline" onClick={seed} data-testid="physio-seed-btn"><Database className="h-4 w-4 mr-2" />Stammdaten laden</Button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-sm ${tab === t.key ? 'bg-emerald-600 text-white' : 'bg-muted'}`}
            data-testid={`physio-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'praxis' && settings && (
        <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label>Praxisname</Label><Input value={sf('company_name')} onChange={e => setS('company_name', e.target.value)} data-testid="physio-set-company" /></div>
          <div><Label>Strasse</Label><Input value={sf('street')} onChange={e => setS('street', e.target.value)} /></div>
          <div><Label>Nr.</Label><Input value={sf('house_no')} onChange={e => setS('house_no', e.target.value)} /></div>
          <div><Label>PLZ</Label><Input value={sf('zip_code')} onChange={e => setS('zip_code', e.target.value)} /></div>
          <div><Label>Ort</Label><Input value={sf('city')} onChange={e => setS('city', e.target.value)} /></div>
          <div><Label>Kanton</Label><Input value={sf('canton')} onChange={e => setS('canton', e.target.value)} /></div>
          <div><Label>Praxis-GLN</Label><Input value={sf('practice_gln')} onChange={e => setS('practice_gln', e.target.value)} data-testid="physio-set-gln" /></div>
          <div><Label>Praxis-ZSR *</Label><Input value={sf('practice_zsr')} onChange={e => setS('practice_zsr', e.target.value)} data-testid="physio-set-zsr" /></div>
          <div><Label>QR-IBAN</Label><Input value={sf('qr_iban')} onChange={e => setS('qr_iban', e.target.value)} /></div>
          <div><Label>MWST-Nr.</Label><Input value={sf('vat_number')} onChange={e => setS('vat_number', e.target.value)} /></div>
          <div><Label>MediData Teilnehmer-ID</Label><Input value={sf('medidata_participant_id')} onChange={e => setS('medidata_participant_id', e.target.value)} /></div>
          <div className="col-span-2 md:col-span-3 border-t pt-3">
            <Label>Praxis-Logo (erscheint auf dem Rückforderungsbeleg)</Label>
            <div className="flex items-center gap-3 mt-1">
              {sf('logo_base64') ? (
                <img src={sf('logo_base64')} alt="Logo" className="h-12 object-contain border rounded bg-white p-1" data-testid="physio-logo-preview" />
              ) : (
                <span className="text-xs text-muted-foreground">Kein Logo hochgeladen</span>
              )}
              <input type="file" accept="image/png,image/jpeg" className="text-sm" data-testid="physio-logo-upload"
                onChange={e => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 1024 * 1024) { toast.error('Logo max. 1 MB'); return; }
                  const r = new FileReader();
                  r.onload = () => setS('logo_base64', r.result);
                  r.readAsDataURL(f);
                }} />
              {sf('logo_base64') && (
                <Button size="sm" variant="ghost" onClick={() => setS('logo_base64', '')} data-testid="physio-logo-remove"><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          </div>
          <div className="col-span-2 md:col-span-3"><Button onClick={saveSettings} data-testid="physio-set-save"><Save className="h-4 w-4 mr-2" />Speichern</Button>
            <span className="ml-3 text-xs text-muted-foreground">Ohne gültige ZSR wird jede Rechnung von der Kasse zurückgewiesen.</span></div>
        </CardContent></Card>
      )}

      {tab === 'medidata' && medidata && (
        <Card><CardContent className="p-4 space-y-4" data-testid="physio-medidata-settings">
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
            <b>MediData Cloud-/REST-Versand (eLA) mit mTLS.</b> Du kannst Test-Zugang (ACC) und
            Live-Zugang parallel hinterlegen und per Schalter umstellen. Die Authentisierung erfolgt
            über das Client-Zertifikat (CN = MasterID) und den privaten Schlüssel – kein Passwort.
            Solange „Anbindung aktiv" nicht gesetzt oder der aktive Zugang unvollständig ist
            (Basis-URL + Client-ID + Zertifikat + Schlüssel), wird der Versand simuliert.
          </div>

          {medidata?.platform_connected && !medidata?.using_own_credentials && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800" data-testid="physio-md-platform-banner">
              <b>Zentrale MediData-Anbindung von StrongFit aktiv</b> (Umgebung: {medidata.platform_environment === 'prod' ? 'Live' : 'Test (ACC)'}). Du musst hier <b>keine Zertifikate</b> eintragen – deine Rechnungen werden automatisch über die zentrale Anbindung versendet. Hinterlege im Tab <b>„Praxis"</b> nur deine eigene <b>Absender-GLN</b>. Eigene Zugangsdaten unten brauchst du nur, wenn du einen <b>eigenen</b> MediData-Vertrag nutzt.
            </div>
          )}
          {medidata?.platform_connected && medidata?.using_own_credentials && (
            <div className="rounded-md bg-sky-50 border border-sky-200 p-3 text-xs text-sky-800" data-testid="physio-md-platform-banner-own">
              Du nutzt <b>eigene</b> MediData-Zugangsdaten (sie überschreiben die zentrale StrongFit-Anbindung). Lösche deine Zertifikate unten, um wieder die zentrale Anbindung zu verwenden.
            </div>
          )}

          <div className="flex flex-wrap gap-6 items-center">
            <label className="flex items-center gap-2 text-sm" data-testid="physio-md-enabled">
              <input type="checkbox" checked={!!mf('enabled')} onChange={e => setM('enabled', e.target.checked)} />
              Anbindung aktiv
            </label>
            <div className="flex items-center gap-2 text-sm" data-testid="physio-md-environment">
              <span className="text-muted-foreground">Aktiver Zugang:</span>
              {[['acc', 'Test (ACC)'], ['prod', 'Live']].map(([v, l]) => (
                <label key={v} className="flex items-center gap-1">
                  <input type="radio" name="md-env" checked={(mf('environment') || 'acc') === v}
                    onChange={() => setM('environment', v)} data-testid={`physio-md-env-${v}`} />
                  {l}
                </label>
              ))}
            </div>
          </div>

          {[['acc', 'Test-Zugang (ACC)', 'https://…acc….medidata.ch'],
            ['prod', 'Live-Zugang', 'https://…prod….medidata.ch']].map(([pre, title, ph]) => (
            <div key={pre} className="border rounded-md p-3 space-y-3" data-testid={`physio-md-block-${pre}`}>
              <div className="font-medium text-sm">{title}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2"><Label>Basis-URL</Label>
                  <Input value={mf(`${pre}_base_url`)} onChange={e => setM(`${pre}_base_url`, e.target.value)}
                    placeholder={ph} data-testid={`physio-md-${pre}-baseurl`} />
                  <span className="text-xs text-muted-foreground">Host-Basis, z. B. …/mdnetconnect – die Pfade /ela/v1/… werden automatisch angehängt.</span></div>
                <div><Label>Client-ID (X-CLIENT-ID)</Label>
                  <Input value={mf(`${pre}_client_id`)} onChange={e => setM(`${pre}_client_id`, e.target.value)}
                    data-testid={`physio-md-${pre}-clientid`} /></div>
                <div className="flex items-end">
                  <span className="text-xs text-muted-foreground">
                    {mf(`has_${pre}_cert`) ? '✓ Zertifikat hinterlegt' : 'Kein Zertifikat'} ·{' '}
                    {mf(`has_${pre}_key`) ? '✓ Schlüssel hinterlegt' : 'Kein Schlüssel'}
                  </span>
                </div>
                <div><Label>Client-Zertifikat (PEM)</Label>
                  <Textarea rows={3} value={mf(`${pre}_cert_pem`)} onChange={e => setM(`${pre}_cert_pem`, e.target.value)}
                    placeholder="-----BEGIN CERTIFICATE-----" className="font-mono text-xs" data-testid={`physio-md-${pre}-cert`} />
                  <input type="file" accept=".pem,.crt,.cer" className="text-xs mt-1" data-testid={`physio-md-${pre}-cert-file`}
                    onChange={e => readPem(`${pre}_cert_pem`, e.target.files?.[0])} /></div>
                <div><Label>Privater Schlüssel (PEM) {mf(`has_${pre}_key`) ? '— gesetzt, leer lassen zum Behalten' : ''}</Label>
                  <Textarea rows={3} value={mf(`${pre}_key_pem`)} onChange={e => setM(`${pre}_key_pem`, e.target.value)}
                    placeholder={mf(`has_${pre}_key`) ? '•••••••• (bereits hinterlegt)' : '-----BEGIN PRIVATE KEY-----'}
                    className="font-mono text-xs" data-testid={`physio-md-${pre}-key`} />
                  <input type="file" accept=".pem,.key" className="text-xs mt-1" data-testid={`physio-md-${pre}-key-file`}
                    onChange={e => readPem(`${pre}_key_pem`, e.target.files?.[0])} /></div>
              </div>
            </div>
          ))}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Master-ID (CN des Zertifikats)</Label>
              <Input value={mf('master_id')} onChange={e => setM('master_id', e.target.value)} data-testid="physio-md-masterid" /></div>
            <div><Label>Absender-GLN</Label>
              <Input value={mf('sender_gln')} onChange={e => setM('sender_gln', e.target.value)} placeholder="leer = Praxis-GLN" data-testid="physio-md-sender" /></div>
            <div><Label>Empfänger-GLN</Label>
              <Input value={mf('recipient_gln')} onChange={e => setM('recipient_gln', e.target.value)} data-testid="physio-md-recipient" />
              <span className="text-xs text-muted-foreground">Standard: ZAS 7601003002751</span></div>
            <div><Label>Timeout (Sek.)</Label>
              <Input type="number" value={mf('timeout_s')} onChange={e => setM('timeout_s', parseInt(e.target.value) || 30)} data-testid="physio-md-timeout" /></div>
          </div>

          <div className="flex gap-3">
            <Button onClick={saveMedidata} data-testid="physio-md-save"><Save className="h-4 w-4 mr-2" />Speichern</Button>
            <Button variant="outline" onClick={testMedidata} disabled={testingConn} data-testid="physio-md-test">
              <PlugZap className="h-4 w-4 mr-2" />{testingConn ? 'Teste…' : 'Verbindung testen'}
            </Button>
          </div>
        </CardContent></Card>
      )}

      {tab === 'feedback' && (
        <Card><CardContent className="p-4 space-y-4" data-testid="physio-medidata-feedback">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Offene Rückmeldungen (Downloads) und Nachrichten (GNOT) vom aktiven MediData-Zugang.
            </div>
            <Button size="sm" variant="outline" onClick={loadFeedback} disabled={feedbackLoading} data-testid="physio-md-feedback-refresh">
              {feedbackLoading ? 'Lade…' : 'Aktualisieren'}
            </Button>
          </div>

          <div>
            <h3 className="font-medium text-sm mb-2">Rückmeldungen / Dokumente</h3>
            {downloads.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="physio-md-downloads-empty">Keine offenen Rückmeldungen.</p>
            ) : (
              <table className="w-full text-sm" data-testid="physio-md-downloads-table">
                <thead><tr className="text-left text-muted-foreground border-b"><th className="py-1 pr-3">Referenz</th><th className="py-1 pr-3">Typ</th><th></th></tr></thead>
                <tbody>
                  {downloads.map((d, i) => {
                    const ref = d.transmissionReference || d.reference || d.id || String(i);
                    return (
                      <tr key={ref} className="border-b">
                        <td className="py-1 pr-3 font-mono text-xs">{ref}</td>
                        <td className="py-1 pr-3">{d.documentType || d.type || '—'}</td>
                        <td><Button size="sm" variant="ghost" onClick={() => confirmDownload(ref)} data-testid={`physio-md-dl-confirm-${ref}`}>Bestätigen</Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <h3 className="font-medium text-sm mb-2">Nachrichten (GNOT)</h3>
            {notifications.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="physio-md-notifications-empty">Keine offenen Nachrichten.</p>
            ) : (
              <table className="w-full text-sm" data-testid="physio-md-notifications-table">
                <thead><tr className="text-left text-muted-foreground border-b"><th className="py-1 pr-3">ID</th><th className="py-1 pr-3">Betreff/Typ</th><th></th></tr></thead>
                <tbody>
                  {notifications.map((n, i) => {
                    const id = n.id || n.notificationId || String(i);
                    return (
                      <tr key={id} className="border-b">
                        <td className="py-1 pr-3 font-mono text-xs">{id}</td>
                        <td className="py-1 pr-3">{n.subject || n.type || n.module || '—'}</td>
                        <td><Button size="sm" variant="ghost" onClick={() => confirmNotification(id)} data-testid={`physio-md-nt-confirm-${id}`}>Bestätigen</Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </CardContent></Card>
      )}

      {tab === 'reminder' && reminder && (
        <Card><CardContent className="p-4 space-y-4" data-testid="physio-reminder-settings">
          <div className="rounded-md bg-sky-50 border border-sky-200 p-3 text-xs text-sky-800 flex items-start gap-2">
            <Mail className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Der Kunde erhält automatisch eine E-Mail, sobald das Restkontingent seiner Verordnung die Schwelle erreicht – damit er rechtzeitig eine neue ärztliche Verordnung besorgt. Pro Verordnung wird nur einmal erinnert. Platzhalter: <code>{'{first_name}'}</code>, <code>{'{sessions_remaining}'}</code>, <code>{'{sessions_granted}'}</code>.</span>
          </div>
          <label className="flex items-center gap-2 text-sm" data-testid="physio-reminder-enabled">
            <input type="checkbox" checked={!!reminder.enabled} onChange={e => setR('enabled', e.target.checked)} />
            Erinnerungs-E-Mail aktiv
          </label>
          <div className="w-48">
            <Label>E-Mail senden, wenn noch … Sitzungen übrig sind</Label>
            <Input type="number" min="0" value={rf('threshold')} onChange={e => setR('threshold', e.target.value)} data-testid="physio-reminder-threshold" />
          </div>
          <div>
            <Label>Betreff</Label>
            <Input value={rf('subject')} onChange={e => setR('subject', e.target.value)} data-testid="physio-reminder-subject" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Nachricht</Label>
              <Button size="sm" variant="outline" onClick={aiDraftReminder} disabled={aiDrafting} data-testid="physio-reminder-ai">
                <Sparkles className="h-4 w-4 mr-2" />{aiDrafting ? 'Erstelle…' : 'Mit KI erstellen'}
              </Button>
            </div>
            <Textarea rows={8} value={rf('body')} onChange={e => setR('body', e.target.value)} data-testid="physio-reminder-body" />
          </div>
          <Button onClick={saveReminder} data-testid="physio-reminder-save"><Save className="h-4 w-4 mr-2" />Speichern</Button>
        </CardContent></Card>
      )}
      {tab === 'insurers' && (
        <StammTable
          rows={insurers} testid="physio-insurers"
          cols={[['name', 'Name'], ['gln', 'GLN'], ['law', 'Gesetz'], ['tiers_default', 'Tiers']]}
          onAdd={(d) => addRow('insurers', { name: d.name, gln: d.gln, law: d.law || 'KVG', tiers_default: d.tiers_default || 'payant' }, setInsurers, insurers)}
          onDel={(id) => delRow('insurers', id, setInsurers, insurers)}
          blank={{ name: '', gln: '', law: 'KVG', tiers_default: 'payant' }} />
      )}
      {tab === 'tariffs' && (
        <StammTable
          rows={tariffs} testid="physio-tariffs"
          cols={[['tariff_type', 'Tarif'], ['code', 'Code'], ['label', 'Bezeichnung'], ['unit', 'Einheit'], ['tax_points', 'TP']]}
          onAdd={(d) => addRow('tariff-positions', { tariff_type: d.tariff_type || '311', code: d.code, label: d.label, unit: d.unit || 'Sitzung', tax_points: parseFloat(d.tax_points) || 0 }, setTariffs, tariffs)}
          onDel={(id) => delRow('tariff-positions', id, setTariffs, tariffs)}
          blank={{ tariff_type: '311', code: '', label: '', unit: 'Sitzung', tax_points: '' }} />
      )}
      {tab === 'taxpoints' && (
        <StammTable
          rows={taxpoints} testid="physio-taxpoints"
          cols={[['canton', 'Kanton'], ['tariff_type', 'Tarif'], ['law', 'Gesetz'], ['value_chf', 'CHF/TP']]}
          onAdd={(d) => addRow('tax-point-values', { canton: d.canton, tariff_type: d.tariff_type || '311', law: d.law || 'KVG', value_chf: parseFloat(d.value_chf) || 1 }, setTaxpoints, taxpoints)}
          onDel={(id) => delRow('tax-point-values', id, setTaxpoints, taxpoints)}
          blank={{ canton: 'ZH', tariff_type: '311', law: 'KVG', value_chf: '1.00' }} />
      )}
    </div>
  );
};

const StammTable = ({ rows, cols, onAdd, onDel, blank, testid }) => {
  const [form, setForm] = useState(blank);
  return (
    <Card><CardContent className="p-4">
      <div className="overflow-auto">
        <table className="w-full text-sm" data-testid={`${testid}-table`}>
          <thead><tr className="text-left text-muted-foreground border-b">
            {cols.map(([k, l]) => <th key={k} className="py-2 pr-3">{l}</th>)}<th></th>
          </tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b">
                {cols.map(([k]) => <td key={k} className="py-2 pr-3">{String(r[k] ?? '')}</td>)}
                <td><Button size="sm" variant="ghost" onClick={() => onDel(r.id)} data-testid={`${testid}-del-${r.id}`}><Trash2 className="h-4 w-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2 mt-3 flex-wrap items-end">
        {cols.map(([k, l]) => (
          <div key={k}><Label className="text-xs">{l}</Label>
            <Input className="h-9 w-32" value={form[k] ?? ''} onChange={e => setForm({ ...form, [k]: e.target.value })} data-testid={`${testid}-input-${k}`} /></div>
        ))}
        <Button size="sm" onClick={() => { onAdd(form); setForm(blank); }} data-testid={`${testid}-add`}><Plus className="h-4 w-4 mr-1" />Hinzufügen</Button>
      </div>
    </CardContent></Card>
  );
};

export default PhysioBillingSettingsPage;
